import { useQuery } from '@tanstack/react-query';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

import { dashboardQueryOptions } from './dashboard-query-options';

interface Group {
  color: Color;
  invoiceCount: number;
  label: Label;
  total: number;
}

export interface Invoice {
  __v: number;
  _id: string;
  appliedCouponId: Array<string>;
  baseAmount: number;
  createdAt: string;
  dueDate?: string;
  endDate: string | null;
  enrollmentTitle?: string;
  /** Present for event-linked invoices (class column uses event vs enrollment title). */
  eventTitle?: string;
  entityId: string;
  entityType: string;
  finalAmount: number;
  /**
   * Backend-computed outstanding amount (finalAmount - payments),
   * rounded to currency. Prefer this over `finalAmount` for AR totals.
   */
  outstandingAmount?: number;
  line_items: Array<LineItem>;
  metadata: Metadata;
  parentId: string;
  paymentDate?: string;
  paymentMethod: string;
  paymentProvider: string;
  payments: Array<unknown>;
  startDate: string | null;
  status: string;
  studentId: string;
  studentName: string;
  studioId: string;
  subscriptionId?: string;
  transactionCodeId: null;
  type: string;
  updatedAt: string;
}

export enum Color {
  Green = 'green',
  Orange = 'orange',
  Red = 'red',
  Yellow = 'yellow',
}

export enum Label {
  Days0To30 = '0-30d',
  Days31To60 = '31-60d',
  Days61To90 = '61-90d',
  Days90Plus = '90+d',
}

export interface LineItem {
  amount: number;
  name: string;
  quantity: number;
  total: number;
  type: string;
}

export interface Metadata {
  appliedCouponId?: Array<string>;
  applyDiscountTo?: string;
  attemptCount?: number;
  discountCategory?: string;
  discountType?: string;
  internalTransactionId: string;
  invoiceGeneratedBy?: string;
  paymentIntentId?: string;
}

export interface AccountsReceivableStudentGroup {
  email: string;
  familyName: string;
  groups: Array<Group>;
  invoices: Array<Invoice>;
  parentId: string;
  phone: string;
  studentId: string;
  studentName: string;
  parentName: string;
}

export interface GetAccountsReceivableStudentGroupingParams {
  sessionId: string;
  range?: Label;
  enabled?: boolean;
}

export const getAccountsReceivableStudentGroupingAPI = async (
  params: GetAccountsReceivableStudentGroupingParams,
) => {
  const response = await baseAPI.get<Array<AccountsReceivableStudentGroup>>(
    '/dashboard/metrics/account-receivable/student-grouping',
    {
      params,
    },
  );
  return response.data;
};

export const useGetAccountsReceivableStudentGrouping = (
  params: GetAccountsReceivableStudentGroupingParams,
) => {
  const { enabled = true, ...apiParams } = params;
  return useQuery({
    ...dashboardQueryOptions,
    queryKey: [
      KEYS.ACCOUNTS_RECEIVABLE_STUDENT_GROUPING,
      params.sessionId,
      params.range,
    ],
    queryFn: () => getAccountsReceivableStudentGroupingAPI(apiParams),
    enabled: enabled && !!params.sessionId,
  });
};
